import { RotationStatus, StageStatus } from '../../models';
import { getStagesByApparatus, sortRosters } from './hooks';
import { getTeamTricode } from './utils';

// ============================================================================
// Session Info Sync
// ============================================================================

export async function syncSessionInfo(
  session,
  { type, alternating },
  { updateSession }
) {
  return updateSession({
    variables: {
      input: {
        id: session.id,
        _version: session._version,
        type,
        alternating,
      },
    },
  });
}

// ============================================================================
// Rosters Sync - Auto-add team rosters and link lineups
// ============================================================================

export async function syncRosters(
  session,
  { sessionTeams, sessionRosters, lineups },
  { createSessionRoster, updateSessionRoster },
  onLog
) {
  const log = (msg) => onLog && onLog(msg);
  const operations = [];

  // Get teams that need rosters
  const teams = sessionTeams.map((st) => st.team).filter(Boolean);
  const existingRosterIds = sessionRosters.map((sr) => sr.roster?.id);

  // Add rosters for teams that don't have one
  for (const team of teams) {
    const teamRosters = sortRosters(team?.rosters?.items || []);
    const hasRoster = teamRosters.some((r) => existingRosterIds.includes(r.id));

    if (!hasRoster && teamRosters.length > 0) {
      const lineup = lineups.find((l) => l.teamId === team.id);

      operations.push(
        createSessionRoster({
          variables: {
            input: {
              sessionId: session.id,
              rosterId: teamRosters[0].id,
              lineupId: lineup?.id,
            },
          },
        }).then(() => {
          log(`Added roster "${teamRosters[0].title}" for ${team.name}`);
          if (lineup) {
            log(`Linked lineup for ${team.name}`);
          }
        })
      );
    }
  }

  // Link lineups to existing rosters that don't have one
  for (const sr of sessionRosters) {
    if (!sr.lineupId && sr.roster?.teamId) {
      const lineup = lineups.find((l) => l.teamId === sr.roster.teamId);
      if (lineup) {
        operations.push(
          updateSessionRoster({
            variables: {
              input: {
                id: sr.id,
                _version: sr._version,
                lineupId: lineup.id,
              },
            },
          }).then(() => {
            log(`Linked lineup to ${sr.roster?.team?.name || 'roster'}`);
          })
        );
      }
    }
  }

  await Promise.all(operations);
}

// ============================================================================
// Order Validation and Repair
// ============================================================================

/**
 * Check if team/lineup orders are corrupted and need repair.
 * Orders are corrupted if:
 * - All orders are the same value (e.g., all 0)
 * - Orders are not unique
 * - Lineup orders don't match sessionTeam orders
 */
function detectCorruptedOrders(sessionTeams, lineups) {
  if (sessionTeams.length <= 1) return { corrupted: false };

  const teamOrders = sessionTeams.map((st) => st.order ?? 0);
  const uniqueTeamOrders = new Set(teamOrders);

  // Check if team orders are all the same or not unique
  const teamOrdersCorrupted =
    uniqueTeamOrders.size !== sessionTeams.length ||
    (uniqueTeamOrders.size === 1 && sessionTeams.length > 1);

  // Check lineup orders
  const lineupOrders = lineups.map((l) => l.order ?? 0);
  const uniqueLineupOrders = new Set(lineupOrders);
  const lineupOrdersCorrupted =
    lineups.length > 1 &&
    (uniqueLineupOrders.size !== lineups.length ||
      (uniqueLineupOrders.size === 1 && lineups.length > 1));

  // Check if lineup orders match sessionTeam orders
  let ordersMismatch = false;
  for (const st of sessionTeams) {
    const lineup = lineups.find((l) => l.teamId === st.teamId);
    if (lineup && lineup.order !== st.order) {
      ordersMismatch = true;
      break;
    }
  }

  return {
    corrupted: teamOrdersCorrupted || lineupOrdersCorrupted || ordersMismatch,
    teamOrdersCorrupted,
    lineupOrdersCorrupted,
    ordersMismatch,
  };
}

/**
 * Repair corrupted orders by assigning unique sequential values.
 * Accepts an optional orderOverride map (teamId → order) to use a user-specified order
 * instead of the default alphabetical sort.
 * Also updates the local objects so subsequent code has correct data.
 */
export async function repairCorruptedOrders(
  sessionTeams,
  lineups,
  { updateSessionTeam, updateLineup },
  log,
  orderOverride
) {
  log('Repairing corrupted orders...');

  // Use user-specified order if provided, otherwise sort alphabetically
  let sortedTeams;
  if (orderOverride) {
    sortedTeams = [...sessionTeams].sort(
      (a, b) =>
        (orderOverride[a.teamId] ?? 999) - (orderOverride[b.teamId] ?? 999)
    );
  } else {
    sortedTeams = [...sessionTeams].sort((a, b) =>
      (a.team?.name || '').localeCompare(b.team?.name || '')
    );
  }

  const repairs = [];
  // Track corrected orders by teamId for use by auto-assign
  const correctedOrders = {};

  for (let i = 0; i < sortedTeams.length; i++) {
    const st = sortedTeams[i];
    const expectedOrder = i;

    // Record the correct order for this team
    correctedOrders[st.teamId] = expectedOrder;

    // Fix sessionTeam order if needed
    if (st.order !== expectedOrder) {
      const oldOrder = st.order;
      repairs.push(
        updateSessionTeam({
          variables: {
            input: {
              id: st.id,
              _version: st._version,
              order: expectedOrder,
            },
          },
        }).then(() => {
          log(
            `  Fixed ${
              st.team?.name || 'team'
            } order: ${oldOrder} → ${expectedOrder}`
          );
        })
      );
    }

    // Fix lineup order if needed
    const lineup = lineups.find((l) => l.teamId === st.teamId);
    if (lineup && lineup.order !== expectedOrder) {
      const oldOrder = lineup.order;
      repairs.push(
        updateLineup({
          variables: {
            input: {
              id: lineup.id,
              _version: lineup._version,
              order: expectedOrder,
            },
          },
        }).then(() => {
          log(
            `  Fixed ${
              st.team?.name || 'team'
            } lineup order: ${oldOrder} → ${expectedOrder}`
          );
        })
      );
    }
  }

  if (repairs.length > 0) {
    await Promise.all(repairs);
    log(`Repaired ${repairs.length} order(s)`);
  }

  // Return the corrected orders map so auto-assign can use it
  return { repaired: repairs.length > 0, correctedOrders };
}

// ============================================================================
// Rotations Sync - Create squads, rotations, stages
// ============================================================================

export async function syncRotations(
  session,
  {
    desiredSquads,
    desiredRotations,
    currentRotations,
    currentSquads,
    sessionRosters,
    sessionTeams,
    lineups,
    autoAssignSquads,
    skipOrderRepair,
  },
  {
    createRotation,
    deleteRotation,
    createSquad,
    deleteSquad,
    createStage,
    updateStage,
    deleteStage,
    updateSessionRoster,
    updateSessionTeam,
    updateLineup,
  },
  onLog
) {
  const log = (msg) => onLog && onLog(msg);

  // Step 0: Detect and repair corrupted orders before any other operations
  // correctedOrders will be a map of teamId → correct order value
  let correctedOrders = null;
  if (!skipOrderRepair) {
    const orderStatus = detectCorruptedOrders(sessionTeams, lineups);
    if (orderStatus.corrupted) {
      log('Warning: Detected corrupted team/lineup orders');
      if (orderStatus.teamOrdersCorrupted)
        log('  - Team orders are not unique');
      if (orderStatus.lineupOrdersCorrupted)
        log('  - Lineup orders are not unique');
      if (orderStatus.ordersMismatch)
        log('  - Team and lineup orders do not match');

      const repairResult = await repairCorruptedOrders(
        sessionTeams,
        lineups,
        { updateSessionTeam, updateLineup },
        log
      );
      correctedOrders = repairResult.correctedOrders;
    }
  } else {
    log('Skipping order repair (handled separately)');
  }

  // Build a mutable copy of current state
  const sortedRotations = [...currentRotations];
  const activeSquadsByName = currentSquads.reduce((acc, squad) => {
    acc[squad.name] = squad;
    return acc;
  }, {});

  // Step 1: Create or delete rotations to match desired count
  if (sortedRotations.length < desiredRotations.length) {
    const ops = [];
    for (let i = sortedRotations.length; i < desiredRotations.length; i++) {
      ops.push(
        createRotation({
          variables: {
            input: {
              sessionId: session.id,
              order: i,
              status: RotationStatus.CREATED,
            },
          },
        }).then(({ data }) => {
          sortedRotations[i] = data.createRotation;
          log(`Created rotation ${i + 1}`);
        })
      );
    }
    await Promise.all(ops);
  } else if (sortedRotations.length > desiredRotations.length) {
    const ops = [];
    for (let i = desiredRotations.length; i < sortedRotations.length; i++) {
      if (sortedRotations[i]) {
        ops.push(
          deleteRotation({
            variables: {
              input: {
                id: sortedRotations[i].id,
                _version: sortedRotations[i]._version,
              },
            },
          }).then(() => {
            log(`Deleted rotation ${i + 1}`);
          })
        );
      }
    }
    await Promise.all(ops);
  }

  // Step 2: Create or delete squads to match desired squads
  const squadOps = [];
  for (const desiredSquad of desiredSquads) {
    if (!activeSquadsByName[desiredSquad]) {
      squadOps.push(
        createSquad({
          variables: {
            input: {
              sessionId: session.id,
              name: desiredSquad,
            },
          },
        }).then(({ data }) => {
          activeSquadsByName[desiredSquad] = data.createSquad;
          log(`Created squad ${desiredSquad}`);
        })
      );
    }
  }

  // Delete squads not in desired list
  for (const squad of currentSquads) {
    if (!desiredSquads.includes(squad.name)) {
      squadOps.push(
        deleteSquad({
          variables: {
            input: {
              id: squad.id,
              _version: squad._version,
            },
          },
        }).then(() => {
          delete activeSquadsByName[squad.name];
          log(`Deleted squad ${squad.name}`);
        })
      );
    }
  }
  await Promise.all(squadOps);

  // Step 3: Sync stages — update squadId in place, only delete/create when necessary
  const activeStagesByApparatusByRotation = [];
  const updateOps = [];
  const deleteOps = [];

  // First pass: for each existing stage, update, keep, or delete
  for (
    let rotationIndex = 0;
    rotationIndex < sortedRotations.length;
    rotationIndex++
  ) {
    const rotation = sortedRotations[rotationIndex];
    if (!rotation || rotationIndex >= desiredRotations.length) continue;

    activeStagesByApparatusByRotation[rotationIndex] = getStagesByApparatus(
      rotation.stages?.items || []
    );

    for (const apparatus of Object.keys(
      activeStagesByApparatusByRotation[rotationIndex]
    )) {
      const stage = activeStagesByApparatusByRotation[rotationIndex][apparatus];
      if (stage._deleted) continue;

      const desiredSquadName = desiredRotations[rotationIndex][apparatus];
      const desiredSquad = desiredSquadName
        ? activeSquadsByName[desiredSquadName]
        : null;

      if (!desiredSquadName) {
        // Apparatus no longer in desired rotation — delete
        delete activeStagesByApparatusByRotation[rotationIndex][apparatus];
        deleteOps.push(
          deleteStage({
            variables: {
              input: { id: stage.id, _version: stage._version },
            },
          })
        );
      } else if (
        desiredSquad &&
        (stage.squadId || stage.squad?.id) !== desiredSquad.id
      ) {
        // Stage exists but squad changed — update in place
        updateOps.push(
          updateStage({
            variables: {
              input: {
                id: stage.id,
                _version: stage._version,
                squadId: desiredSquad.id,
              },
            },
          }).then(({ data }) => {
            activeStagesByApparatusByRotation[rotationIndex][apparatus] =
              data.updateStage;
            log(
              `Updated stage ${apparatus} rotation ${rotationIndex} → squad ${desiredSquadName}`
            );
          })
        );
      }
      // else: stage already has correct squad — keep as-is
    }
  }
  await Promise.all([...updateOps, ...deleteOps]);

  // Second pass: create stages that don't exist yet
  const createOps = [];
  for (
    let rotationIndex = 0;
    rotationIndex < sortedRotations.length;
    rotationIndex++
  ) {
    const rotation = sortedRotations[rotationIndex];
    if (!rotation || rotationIndex >= desiredRotations.length) continue;

    for (const apparatus of Object.keys(desiredRotations[rotationIndex])) {
      if (!activeStagesByApparatusByRotation[rotationIndex][apparatus]) {
        const squadName = desiredRotations[rotationIndex][apparatus];
        if (activeSquadsByName[squadName]) {
          createOps.push(
            createStage({
              variables: {
                input: {
                  sessionId: session.id,
                  rotationId: rotation.id,
                  status: StageStatus.CREATED,
                  squadId: activeSquadsByName[squadName].id,
                  apparatus,
                },
              },
            }).then(({ data }) => {
              activeStagesByApparatusByRotation[rotationIndex][apparatus] =
                data.createStage;
            })
          );
        }
      }
    }
  }
  await Promise.all(createOps);

  // Step 4: Auto-assign rosters to squads if enabled
  if (autoAssignSquads) {
    // Log current orders (should be valid after Step 0 repair)
    log('Current orders:');
    for (const st of sessionTeams) {
      const lineup = lineups.find((l) => l.teamId === st.teamId);
      log(
        `  ${st.team?.name || 'Unknown'}: sessionTeam.order=${
          st.order
        }, lineup.order=${lineup?.order ?? 'N/A'}`
      );
    }

    const assignOps = [];

    // Helper to get the correct order for a team
    // Use correctedOrders if orders were repaired, otherwise use sessionTeam.order
    const getTeamOrder = (teamId) => {
      if (correctedOrders && correctedOrders[teamId] !== undefined) {
        return correctedOrders[teamId];
      }
      const st = sessionTeams.find((st) => st.teamId === teamId);
      return st?.order ?? 999;
    };

    // Sort rosters by team order for consistent positioning
    const sortedRosters = [...sessionRosters].sort((a, b) => {
      const teamIdA = a.roster?.teamId || a.roster?.team?.id;
      const teamIdB = b.roster?.teamId || b.roster?.team?.id;
      return getTeamOrder(teamIdA) - getTeamOrder(teamIdB);
    });

    for (let i = 0; i < sortedRosters.length; i++) {
      const sr = sortedRosters[i];
      const teamId = sr.roster?.teamId || sr.roster?.team?.id;
      const teamOrder = getTeamOrder(teamId);

      // For head-to-head (non-alternating) dual meets with 1 squad,
      // all teams compete in the same squad (Squad A)
      // For multi-squad meets, assign teams to squads based on team order
      let squadName;
      let squadStartPosition = 0;
      if (desiredSquads.length === 1) {
        // Single squad: all teams go to Squad A, use team order for position
        squadName = desiredSquads[0];
        squadStartPosition = teamOrder;
      } else if (teamOrder < desiredSquads.length) {
        // Multiple squads: assign based on team order (team 0 → Squad A, etc.)
        squadName = desiredSquads[teamOrder];
        squadStartPosition = 0;
      }

      if (squadName && activeSquadsByName[squadName]) {
        // Track the assignment for later sessionTeam/lineup updates
        const teamId = sr.roster?.teamId || sr.roster?.team?.id;
        assignOps.push(
          updateSessionRoster({
            variables: {
              input: {
                id: sr.id,
                _version: sr._version,
                squadId: activeSquadsByName[squadName].id,
                squadStartPosition,
              },
            },
          }).then(() => {
            log(
              `Assigned ${
                sr.roster?.team?.name || 'roster'
              } to squad ${squadName} (position ${squadStartPosition})`
            );
            return { teamId, squadStartPosition };
          })
        );
      }
    }
    const assignments = await Promise.all(assignOps);

    // Step 5: Update sessionTeam and lineup orders to match squad positions
    // Only do this for single-squad meets where all teams share one squad
    // For multi-squad meets (one team per squad), keep original orders
    if (desiredSquads.length === 1) {
      const orderOps = [];
      for (const assignment of assignments) {
        if (!assignment) continue;
        const { teamId, squadStartPosition } = assignment;

        // Update sessionTeam order
        const sessionTeam = sessionTeams.find((st) => st.teamId === teamId);
        if (sessionTeam && sessionTeam.order !== squadStartPosition) {
          orderOps.push(
            updateSessionTeam({
              variables: {
                input: {
                  id: sessionTeam.id,
                  _version: sessionTeam._version,
                  order: squadStartPosition,
                },
              },
            }).then(() => {
              log(
                `Updated ${sessionTeam.team?.name || 'team'} order: ${
                  sessionTeam.order
                } → ${squadStartPosition}`
              );
            })
          );
        }

        // Update lineup order
        const lineup = lineups.find((l) => l.teamId === teamId);
        if (lineup && lineup.order !== squadStartPosition) {
          orderOps.push(
            updateLineup({
              variables: {
                input: {
                  id: lineup.id,
                  _version: lineup._version,
                  order: squadStartPosition,
                },
              },
            }).then(() => {
              log(
                `Updated ${lineup.team?.name || 'lineup'} lineup order: ${
                  lineup.order
                } → ${squadStartPosition}`
              );
            })
          );
        }
      }
      if (orderOps.length > 0) {
        await Promise.all(orderOps);
      }
    }
  }

  log('Rotations sync complete');
}

// ============================================================================
// Session Diagnosis - Dry-run analysis without mutations
// ============================================================================

/**
 * Analyze the session configuration and return a structured report of
 * what needs to be fixed, without executing any mutations.
 */
export function diagnoseSession({
  session,
  sessionTeams,
  sessionRosters,
  lineups,
  desiredSquads,
  desiredRotations,
  currentRotations,
  currentSquads,
  autoAssignSquads,
}) {
  const result = {
    orderIssues: { detected: false },
    rosterIssues: { detected: false },
    rotationIssues: { detected: false },
    squadAssignment: { detected: false },
  };

  // --- 1. Order Issues ---
  const orderStatus = detectCorruptedOrders(sessionTeams, lineups);
  if (orderStatus.corrupted) {
    // Compute proposed order (alphabetical by team name, same as repairCorruptedOrders)
    const sortedTeams = [...sessionTeams].sort((a, b) =>
      (a.team?.name || '').localeCompare(b.team?.name || '')
    );

    const proposedTeamOrder = sortedTeams.map((st, i) => ({
      teamId: st.teamId,
      sessionTeamId: st.id,
      name: st.team?.name || 'Unknown',
      tricode: st.team?.triCode || getTeamTricode(st.team?.name),
      currentOrder: st.order ?? 0,
      newOrder: i,
      changed: (st.order ?? 0) !== i,
    }));

    const proposedLineupOrder = sortedTeams.map((st, i) => {
      const lineup = lineups.find((l) => l.teamId === st.teamId);
      return {
        teamId: st.teamId,
        lineupId: lineup?.id,
        name: st.team?.name || 'Unknown',
        tricode: st.team?.triCode || getTeamTricode(st.team?.name),
        currentOrder: lineup?.order ?? 0,
        newOrder: i,
        changed: lineup ? (lineup.order ?? 0) !== i : false,
      };
    });

    result.orderIssues = {
      detected: true,
      teamOrdersCorrupted: orderStatus.teamOrdersCorrupted,
      lineupOrdersCorrupted: orderStatus.lineupOrdersCorrupted,
      ordersMismatch: orderStatus.ordersMismatch,
      proposedTeamOrder,
      proposedLineupOrder,
    };
  }

  // --- 2. Roster Issues ---
  const teams = sessionTeams.map((st) => st.team).filter(Boolean);
  const existingRosterIds = sessionRosters.map((sr) => sr.roster?.id);

  const missingRosters = [];
  for (const team of teams) {
    const teamRosters = sortRosters(team?.rosters?.items || []);
    const hasRoster = teamRosters.some((r) => existingRosterIds.includes(r.id));
    if (!hasRoster && teamRosters.length > 0) {
      missingRosters.push({
        teamName: team.name,
        tricode: team.triCode || getTeamTricode(team.name),
        suggestedRoster: teamRosters[0]?.title || 'Default',
      });
    }
  }

  const unlinkedLineups = [];
  for (const sr of sessionRosters) {
    if (!sr.lineupId && sr.roster?.teamId) {
      const lineup = lineups.find((l) => l.teamId === sr.roster.teamId);
      if (lineup) {
        unlinkedLineups.push({
          teamName: sr.roster?.team?.name || 'Unknown',
          tricode:
            sr.roster?.team?.triCode || getTeamTricode(sr.roster?.team?.name),
        });
      }
    }
  }

  if (missingRosters.length > 0 || unlinkedLineups.length > 0) {
    result.rosterIssues = {
      detected: true,
      missingRosters,
      unlinkedLineups,
    };
  }

  // --- 3. Rotation Issues ---
  const activeSquadsByName = currentSquads.reduce((acc, squad) => {
    acc[squad.name] = squad;
    return acc;
  }, {});

  const rotationsToCreate = Math.max(
    0,
    desiredRotations.length - currentRotations.length
  );
  const rotationsToDelete = Math.max(
    0,
    currentRotations.length - desiredRotations.length
  );

  const squadsToCreate = desiredSquads.filter((s) => !activeSquadsByName[s]);
  const squadsToDelete = currentSquads
    .filter((s) => !desiredSquads.includes(s.name))
    .map((s) => s.name);

  // Count stages that would be updated, deleted, or created
  let stagesToUpdate = 0;
  let stagesToDelete = 0;
  let stagesToCreate = 0;
  let hasExistingJudgeAssignments = false;

  for (
    let ri = 0;
    ri < Math.min(currentRotations.length, desiredRotations.length);
    ri++
  ) {
    const rotation = currentRotations[ri];
    if (!rotation) continue;
    const stagesByApparatus = getStagesByApparatus(
      rotation.stages?.items || []
    );

    for (const apparatus of Object.keys(stagesByApparatus)) {
      const stage = stagesByApparatus[apparatus];
      if (stage._deleted) continue;

      const desiredSquadName = desiredRotations[ri]?.[apparatus];
      const desiredSquad = desiredSquadName
        ? activeSquadsByName[desiredSquadName]
        : null;

      // Use squadId with fallback to squad relation
      const stageSquadId = stage.squadId || stage.squad?.id;

      if (!desiredSquadName) {
        // Apparatus no longer needed — will be deleted
        stagesToDelete++;
        const judges = (stage.judges?.items || []).filter((j) => !j._deleted);
        if (judges.length > 0) {
          hasExistingJudgeAssignments = true;
        }
      } else if (desiredSquad && stageSquadId !== desiredSquad.id) {
        // Squad changed — will be updated in place (preserves judge assignments)
        stagesToUpdate++;
      }
    }

    // Count stages to create
    if (desiredRotations[ri]) {
      for (const apparatus of Object.keys(desiredRotations[ri])) {
        if (!stagesByApparatus[apparatus]) {
          stagesToCreate++;
        }
      }
    }
  }

  // Count stages for new rotations
  for (let ri = currentRotations.length; ri < desiredRotations.length; ri++) {
    if (desiredRotations[ri]) {
      stagesToCreate += Object.keys(desiredRotations[ri]).length;
    }
  }

  // Count stages to delete for excess rotations
  for (let ri = desiredRotations.length; ri < currentRotations.length; ri++) {
    const rotation = currentRotations[ri];
    if (!rotation) continue;
    const stages = (rotation.stages?.items || []).filter((s) => !s._deleted);
    stagesToDelete += stages.length;
    for (const stage of stages) {
      const judges = (stage.judges?.items || []).filter((j) => !j._deleted);
      if (judges.length > 0) hasExistingJudgeAssignments = true;
    }
  }

  const rotationChangesNeeded =
    rotationsToCreate > 0 ||
    rotationsToDelete > 0 ||
    squadsToCreate.length > 0 ||
    squadsToDelete.length > 0 ||
    stagesToUpdate > 0 ||
    stagesToDelete > 0 ||
    stagesToCreate > 0;

  if (rotationChangesNeeded) {
    result.rotationIssues = {
      detected: true,
      rotationsToCreate,
      rotationsToDelete,
      squadsToCreate,
      squadsToDelete,
      stagesToCreate,
      stagesToUpdate,
      stagesToDelete,
      hasExistingJudgeAssignments,
    };
  }

  // --- 4. Squad Assignment ---
  if (autoAssignSquads) {
    const getTeamOrder = (teamId) => {
      // Use proposed order if orders are corrupted, otherwise current
      if (result.orderIssues.detected) {
        const proposed = result.orderIssues.proposedTeamOrder?.find(
          (p) => p.teamId === teamId
        );
        if (proposed) return proposed.newOrder;
      }
      const st = sessionTeams.find((st) => st.teamId === teamId);
      return st?.order ?? 999;
    };

    const sortedRosters = [...sessionRosters].sort((a, b) => {
      const teamIdA = a.roster?.teamId || a.roster?.team?.id;
      const teamIdB = b.roster?.teamId || b.roster?.team?.id;
      return getTeamOrder(teamIdA) - getTeamOrder(teamIdB);
    });

    const assignments = [];
    for (const sr of sortedRosters) {
      const teamId = sr.roster?.teamId || sr.roster?.team?.id;
      const teamOrder = getTeamOrder(teamId);
      const teamName = sr.roster?.team?.name || 'Unknown';
      const tricode = sr.roster?.team?.triCode || getTeamTricode(teamName);

      // Current squad
      const currentSquad = currentSquads.find((s) => s.id === sr.squadId);
      const currentSquadName = currentSquad?.name || null;

      let newSquadName = null;
      let newPosition = 0;
      if (desiredSquads.length === 1) {
        newSquadName = desiredSquads[0];
        newPosition = teamOrder;
      } else if (teamOrder < desiredSquads.length) {
        newSquadName = desiredSquads[teamOrder];
        newPosition = 0;
      }

      const changed =
        currentSquadName !== newSquadName ||
        sr.squadStartPosition !== newPosition;

      assignments.push({
        teamName,
        tricode,
        currentSquad: currentSquadName,
        newSquad: newSquadName,
        currentPosition: sr.squadStartPosition ?? 0,
        newPosition,
        changed,
      });
    }

    const hasChanges = assignments.some((a) => a.changed);
    if (hasChanges) {
      result.squadAssignment = {
        detected: true,
        assignments,
      };
    }
  }

  return result;
}

// ============================================================================
// Master Sync - Run all syncs in sequence
// ============================================================================

export async function syncAll(
  session,
  {
    type,
    alternating,
    sessionTeams,
    sessionRosters,
    lineups,
    desiredSquads,
    desiredRotations,
    currentRotations,
    currentSquads,
    autoAssignSquads,
  },
  mutations,
  onLog,
  refetch
) {
  const log = (msg) => onLog && onLog(msg);

  try {
    log('Starting full configuration...');

    // Step 1: Sync session info
    log('Updating session info...');
    await syncSessionInfo(session, { type, alternating }, mutations);

    // Step 2: Sync rosters
    log('Syncing rosters...');
    await syncRosters(
      session,
      { sessionTeams, sessionRosters, lineups },
      mutations,
      log
    );

    // Refetch fresh data after roster sync so rotation step sees newly created rosters
    let freshSessionRosters = sessionRosters;
    let freshSessionTeams = sessionTeams;
    let freshLineups = lineups;
    let freshSession = session;
    if (refetch) {
      const { data: freshData } = await refetch();
      freshSession = freshData?.SessionByKey?.items?.[0] || session;
      freshSessionRosters =
        freshSession?.rosters?.items?.filter((sr) => !sr._deleted) || [];
      freshSessionTeams =
        freshSession?.sessionTeams?.items?.filter((st) => !st._deleted) || [];
      freshLineups =
        freshSession?.lineups?.items?.filter((l) => !l._deleted) || [];
    }

    // Step 3: Sync rotations
    log('Syncing rotations...');
    await syncRotations(
      freshSession,
      {
        desiredSquads,
        desiredRotations,
        currentRotations,
        currentSquads,
        sessionRosters: freshSessionRosters,
        sessionTeams: freshSessionTeams,
        lineups: freshLineups,
        autoAssignSquads,
      },
      mutations,
      log
    );

    log('Configuration complete!');
    return { success: true };
  } catch (error) {
    log(`Error: ${error.message}`);
    console.error('Sync error:', error);
    return { success: false, error };
  }
}
